// src/pages/CallLogs.tsx

import React, { useState, useEffect } from 'react';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Calendar,
  Building2
} from 'lucide-react';
import { callsService } from 'api/services/calls.service';
import { CallLog, PaginatedResponse, FilterParams } from 'types';
import { useApi } from 'hooks/useApi';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import ErrorAlert from 'components/Common/ErrorAlert';
import Pagination from 'components/Common/Pagination';
import CallLogsFilterBar from 'components/Filters/CallLogsFilterBar';
import CallDetailsModal from 'components/Modals/CallDetailsModal';
import OrganizationFilter from 'components/Filters/OrganizationFilter';
import { formatCallDuration, formatDate, getStatusColor, getDefaultDateRange, categorizeApiError } from 'utils/helpers';
import { ITEMS_PER_PAGE, FEATURE_FLAGS } from 'utils/constants';

const CallLogs: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<FilterParams>({
    perPage: ITEMS_PER_PAGE,
    ...getDefaultDateRange() // Add default 2-day filter (configurable via DEFAULT_DATE_RANGE_DAYS)
  });
  const [selectedCall, setSelectedCall] = useState<CallLog | null>(null);
  // Organization selector - default to current organization
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>(
    FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
      ? (currentOrganization?.id || '')
      : 'all'
  );
  // No local organization list here: the picker is <OrganizationFilter>, which
  // loads its own (and handles master_admin, which the list removed from here
  // did not). Keeping a second, half-populated copy around cost an API call on
  // every page load and read like the source of the org dropdown, which sent me
  // the wrong way while chasing the master-admin listing failure.

  // Determine organizationId to pass to API
  const getOrganizationId = () => {
    if (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') {
      // When REQUIRE_ORG_SELECTION is enabled, selectedOrgId should never be 'all' or empty
      // Return undefined for 'all' or empty string to prevent invalid API calls
      if (selectedOrgId === 'all' || !selectedOrgId) {
        return undefined;
      }
      return selectedOrgId;
    }
    // For other roles, only return valid organization IDs (not synthetic)
    if (currentOrganization?.id && currentOrganization.id !== 'synthetic-org') {
      return currentOrganization.id;
    }
    return undefined;
  };

  // Check if organization selection is required but not selected
  // service_admin belongs here with the other two. getOrganizationId() already
  // treats all three the same — it returns undefined until an organisation is
  // picked — but only master and super admins were ever ASKED to pick one. A
  // service provider therefore got no prompt and no organisationId, and the
  // backend's no-organisation path reads the LOCAL call tables rather than
  // federating to the switches, which is where this estate's calls actually
  // live. The page came back empty and said nothing.
  const needsOrgSelection = FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA &&
    (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') &&
    (!selectedOrgId || selectedOrgId === 'all');

  // Fetch call logs - skip if selection is required
  const { data, loading, error, refetch } = useApi<PaginatedResponse<CallLog>>(
    () => {
      // Skip API call if organization selection is required but not selected
      if (needsOrgSelection) {
        return Promise.resolve({ success: true, data: { data: [], total: 0, totalPages: 0, currentPage: 1 } });
      }
      return callsService.getCallLogs({ ...filters, page: currentPage, organizationId: getOrganizationId() });
    },
    [currentPage, filters.callType, filters.status, filters.dateFrom, filters.dateTo, filters.dialedNumber, filters.callerId, filters.callId, selectedOrgId, currentOrganization?.id, needsOrgSelection]
  );

  const handleFilterChange = (newFilters: FilterParams) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Categorize error for better handling
  const categorizedError = categorizeApiError(error);
  const isOrgNotFoundError = categorizedError?.type === 'organization_not_found';

  if (loading) return <LoadingSpinner />;
  // For "Organization not found" error, continue rendering to show organization filter
  // For other errors, show full error page unless it's a categorized error that should keep UI visible
  if (error && !isOrgNotFoundError && categorizedError?.type !== 'permission') {
    return <ErrorMessage message={error} onRetry={refetch} />;
  }
  if (!data && !categorizedError) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-white">
          Call Logs {data ? <span className="text-gray-500 dark:text-gray-400 text-base lg:text-xl">({data.total})</span> : ''}
        </h1>
        <button
          onClick={refetch}
          className="px-3 lg:px-4 py-2 text-xs lg:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
        >
          Refresh
        </button>
      </div>

      {/* Organization Filter - For master_admin, super_admin, and service_admin */}
      {(currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <OrganizationFilter
            value={selectedOrgId === 'all' ? '' : selectedOrgId}
            onChange={(value) => {
              // Feature Flag: When REQUIRE_ORG_SELECTION is enabled, don't allow 'all'
              if (FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA) {
                setSelectedOrgId(value || selectedOrgId); // Keep current if empty
              } else {
                setSelectedOrgId(value || 'all'); // Legacy: allow 'all'
              }
              setCurrentPage(1);
            }}
            className="w-full md:w-96"
            requireSelection={FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show friendly message when organization selection is required */}
      {needsOrgSelection && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Building2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300">Select an Organization</h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                Please select an organization from the dropdown above to view call logs.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Show error message if there's an error, but keep filters visible for certain error types */}
      {!needsOrgSelection && categorizedError && (
        <ErrorAlert
          error={categorizedError}
          onRetry={refetch}
          className="mb-4"
        />
      )}

      {/* Filters - Only show if we have data or if error allows UI to remain visible */}
      {!needsOrgSelection && !categorizedError && <CallLogsFilterBar filters={filters} onFilterChange={handleFilterChange} />}

      {/* Call Logs - Only show if we have data and no error */}
      {!needsOrgSelection && !categorizedError && data && <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {/* Mobile Card View */}
        <div className="lg:hidden">
          {data.data.length > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.data.map((call) => (
                <div
                  key={call.id}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition cursor-pointer"
                  onClick={() => setSelectedCall(call)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${call.call_type === 'IN'
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400'
                          : 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-400'
                        }`}>
                        {call.call_type === 'IN' ? (
                          <PhoneIncoming className="w-3 h-3" />
                        ) : (
                          <PhoneOutgoing className="w-3 h-3" />
                        )}
                        {call.call_type === 'IN' ? 'Incoming' : 'Outgoing'}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(call.call_status)
                        }`}>
                        {call.call_status}
                      </span>
                    </div>
                    {/* A plain `call.duration && ...` renders a literal "0" for
                        a zero-second call, because React prints the number
                        rather than skipping it. */}
                    {call.duration != null && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatCallDuration(call.duration)}
                      </span>
                    )}
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500 dark:text-gray-400 text-xs">From:</span>
                      <span className="text-gray-900 dark:text-white font-medium">{call.caller_id}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500 dark:text-gray-400 text-xs">To:</span>
                      <span className="text-gray-900 dark:text-white font-medium">{call.dialed_number}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(call.start_time)}
                    </span>
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                      View Details
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Phone className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">No Call Logs Found</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {(currentRole === 'master_admin' || currentRole === 'super_admin')
                  ? 'No calls found. Try adjusting filters.'
                  : 'No call logs found for the selected filters.'}
              </p>
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Call ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Caller
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Dialed Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Start Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  End Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {data.data.map((call) => (
                <tr key={call.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                  {/* Call ID */}
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                    {call.unique_id}
                  </td>

                  {/* Call Type */}
                  <td className="px-6 py-4 text-sm">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${call.call_type === 'IN'
                        ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400'
                        : 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-400'
                      }`}>
                      {call.call_type === 'IN' ? (
                        <PhoneIncoming className="w-3 h-3" />
                      ) : (
                        <PhoneOutgoing className="w-3 h-3" />
                      )}
                      {call.call_type}
                    </span>
                  </td>

                  {/* Caller ID */}
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    <div className="flex items-center gap-1">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {call.caller_id}
                    </div>
                  </td>

                  {/* Dialed Number */}
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    {call.dialed_number}
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4 text-sm">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(call.call_status)
                      }`}>
                      {call.call_status}
                    </span>
                  </td>

                  {/* Duration */}
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    {formatCallDuration(call.duration)}
                  </td>

                  {/* Start Time */}
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {formatDate(call.start_time)}
                    </div>
                  </td>

                  {/* End Time */}
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    {call.end_time ? (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(call.end_time)}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 text-sm">
                    <button
                      onClick={() => setSelectedCall(call)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 font-medium"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}

              {/* Empty state */}
              {data.data.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Phone className="w-12 h-12 text-gray-400" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No Call Logs Found</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {(currentRole === 'master_admin' || currentRole === 'super_admin')
                            ? 'No calls found for the selected business and filters. Try selecting a different business or adjusting your date range.'
                            : 'No call logs found for the selected filters. Try adjusting your date range or filters.'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data.data.length > 0 && (
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <Pagination
              currentPage={currentPage}
              totalPages={data.totalPages}
              onPageChange={handlePageChange}
              totalItems={data.total}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </div>
        )}
      </div>}

      {/* Call Details Modal */}
      {selectedCall && (
        <CallDetailsModal
          call={selectedCall}
          onClose={() => setSelectedCall(null)}
          organizationId={getOrganizationId()}
        />
      )}
    </div>
  );
};

export default CallLogs;